// API Configuration
const API_BASE_URL = '/api';

// Types
export interface Template {
  id: string;
  name: string;
  description: string;
}

// Comic workflow types
export interface CharacterProfile {
  name: string;
  appearance: string;
  personality: string;
}

export interface StoryAnalysis {
  genre: string;
  tone: string;
  era: string;
  themes: string[];
  characters: CharacterProfile[];
}

export interface PanelSpec {
  panelNumber: number;
  narrativeGoal: string;
  sceneDescription: string;
  characters: string[];
  emotion: string;
  camera: string;
  dialogue?: string;
  visualFocus: string;
}

export interface StyleSpec {
  artStyle: string;
  lineStyle: string;
  colorPalette: string;
  lighting: string;
  stylePrompt: string;
  negativeKeywords: string[];
}

// Comic analysis (from comic-graph workflow)
export interface ComicAnalysis {
  storyAnalysis: StoryAnalysis;
  panels: PanelSpec[];
  style: StyleSpec;
}

// Legacy analysis (from original workflow)
export interface LegacyAnalysis {
  imageCount: number;
  reasoning: string;
  images: Array<{ description: string; prompt: string }>;
}

export interface Project {
  id: string;
  name: string;
  templateId: string;
  inputText: string;
  status: 'pending' | 'analyzing' | 'generating' | 'completed' | 'failed';
  // Analysis can be either comic format or legacy format
  analysis: ComicAnalysis | LegacyAnalysis | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Image {
  id: string;
  projectId: string;
  prompt: string;
  description: string | null;
  filePath: string | null;
  status: 'pending' | 'generating' | 'completed' | 'failed';
  error: string | null;
  createdAt: string;
}

export interface ProjectDetail extends Project {
  images: Image[];
}

// Collection types
export interface Collection {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
}

export interface CollectionItem {
  id: string;
  collectionId: string;
  itemType: 'project' | 'collection';
  itemId: string;
  createdAt: string;
}

export interface CollectionDetail extends Collection {
  items: Array<{
    item: CollectionItem;
    data: Project | Collection;
  }>;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  details?: unknown;
}

// Type guard to check if analysis is comic format
export function isComicAnalysis(analysis: ComicAnalysis | LegacyAnalysis | null): analysis is ComicAnalysis {
  return analysis !== null && 'storyAnalysis' in analysis && 'panels' in analysis && 'style' in analysis;
}

// Type guard to check if analysis is legacy format
export function isLegacyAnalysis(analysis: ComicAnalysis | LegacyAnalysis | null): analysis is LegacyAnalysis {
  return analysis !== null && 'imageCount' in analysis && 'reasoning' in analysis && 'images' in analysis;
}

// Base fetch function with authentication via cookies
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include', // Important: Include cookies for auth
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401) {
      window.location.href = '/login';
      return { success: false, error: 'Unauthorized' };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

// API Client
export const api = {
  // Templates
  templates: {
    list: () => apiFetch<Template[]>('/templates'),
    get: (id: string) => apiFetch<Template>(`/templates/${id}`),
  },

  // Projects
  projects: {
    list: () => apiFetch<Project[]>('/projects'),
    
    create: (data: { 
      name?: string; 
      templateId: string; 
      inputText: string; 
      collectionId?: string; 
      systemPrompt?: string;
      baziInput?: {
        name?: string;
        gender: 'Male' | 'Female';
        birthYear: string;
        yearPillar: string;
        monthPillar: string;
        dayPillar: string;
        hourPillar: string;
        startAge: string;
        firstDaYun: string;
      };
    }) =>
      apiFetch<Project>('/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    
    get: (id: string) => apiFetch<ProjectDetail>(`/projects/${id}`),
    
    delete: (id: string) =>
      apiFetch<{ message: string }>(`/projects/${id}`, {
        method: 'DELETE',
      }),
  },

  // Collections
  collections: {
    list: () => apiFetch<Collection[]>('/collections'),
    
    get: (id: string) => apiFetch<CollectionDetail>(`/collections/${id}`),
    
    create: (data: { name: string; description?: string }) =>
      apiFetch<Collection>('/collections', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    
    update: (id: string, data: { name?: string; description?: string }) =>
      apiFetch<Collection>(`/collections/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    
    delete: (id: string) =>
      apiFetch<{ message: string }>(`/collections/${id}`, {
        method: 'DELETE',
      }),
    
    addItem: (id: string, data: { itemType: 'project' | 'collection'; itemId: string }) =>
      apiFetch<CollectionItem>(`/collections/${id}/items`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    
    removeItem: (id: string, itemId: string) =>
      apiFetch<{ message: string }>(`/collections/${id}/items/${itemId}`, {
        method: 'DELETE',
      }),
  },

  // Images
  images: {
    listByProject: (projectId: string) =>
      apiFetch<Image[]>(`/projects/${projectId}/images`),
    
    get: (id: string) => apiFetch<Image>(`/images/${id}`),
    
    getFileUrl: (id: string) => `${API_BASE_URL}/images/${id}/file`,
  },
};

// Helper to get image URL with API key (for img tags)
export function getImageUrl(imageId: string): string {
  return `${API_BASE_URL}/images/${imageId}/file`;
}

// Custom fetch for image loading with credentials
export async function fetchImage(imageId: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/images/${imageId}/file`, {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to load image');
  }
  
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}
